import { useQueryClient } from "@tanstack/react-query";
import { ListTree, Loader2, Sparkles } from "lucide-react";
import { FC, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { getListDocumentsQueryKey } from "~/api/default/default";
import { useRestartDocumentPipeline } from "~/api/documents/documents";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { useProjectParams } from "~/helpers";
import { useToast } from "~/hooks/use-toast";
import { trackEvent } from "~/utils/analytics";

type ReprocessMode = "USE_TOC" | "USE_SNOWFLAKE";

interface ReprocessCaseDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  documentId: string;
  hasBookmarks: boolean;
}

export const ReprocessCaseDialog: FC<ReprocessCaseDialogProps> = ({
  open,
  setOpen,
  documentId,
  hasBookmarks,
}) => {
  const { organizationId, caseId } = useProjectParams();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { formatMessage } = useIntl();
  const [mode, setMode] = useState<ReprocessMode>(
    hasBookmarks ? "USE_TOC" : "USE_SNOWFLAKE"
  );
  const [error, setError] = useState<string | null>(null);
  const { mutateAsync: restartDocumentPipeline, isPending: isLoading } =
    useRestartDocumentPipeline();

  const handleSubmit = async () => {
    setError(null);

    trackEvent({
      eventName: "app/case/actions_menu/reprocess.button/click",
      properties: { caseId, documentId, mode },
    });

    try {
      // USE_TOC means preserve original structure (skipTOC: false)
      // USE_SNOWFLAKE means smart auto-categorization (skipTOC: true)
      const skipTOC = mode === "USE_SNOWFLAKE";

      await restartDocumentPipeline({
        organizationId,
        projectId: caseId,
        documentId,
        params: { skipTOC },
      });

      await queryClient.invalidateQueries({
        queryKey: getListDocumentsQueryKey(organizationId, caseId),
      });

      toast({
        title: formatMessage({ id: "success" }),
        description: formatMessage({
          id: "reprocessDocument.toast.success",
          defaultMessage: "Reprocessing started. Updates will appear shortly.",
        }),
      });

      setOpen(false);
    } catch {
      setError(
        formatMessage({
          id: "reprocessDocument.toast.error",
          defaultMessage: "Failed to start reprocessing. Please try again.",
        })
      );
    }
  };

  const handleOptionClick = (selectedMode: ReprocessMode) => {
    if (selectedMode === "USE_TOC" && !hasBookmarks) {
      return;
    }
    setMode(selectedMode);
    trackEvent({
      eventName: "app/reprocess_modal/option.select",
      properties: {
        caseId,
        documentId,
        selectedMode,
        hasBookmarks,
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            <FormattedMessage
              id="reprocessDocument.title"
              defaultMessage="Reprocess Document"
            />
          </DialogTitle>
          <DialogDescription>
            <FormattedMessage
              id="reprocessDocument.description"
              defaultMessage="Choose how you want Sky AI to organize this document."
            />
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          {/* Option A: Preserve Original Structure (USE_TOC) */}
          <div
            onClick={() => handleOptionClick("USE_TOC")}
            className={`
              relative flex cursor-pointer rounded-lg border p-4 shadow-sm focus:outline-none
              ${mode === "USE_TOC" ? "border-sky-600 ring-1 ring-sky-600 bg-sky-50" : "border-slate-300 hover:bg-slate-50"}
              ${!hasBookmarks ? "opacity-50 cursor-not-allowed bg-slate-100" : ""}
            `}
            role="button"
            tabIndex={hasBookmarks ? 0 : -1}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                handleOptionClick("USE_TOC");
              }
            }}
          >
            <div className="flex w-full items-center justify-between">
              <div className="flex items-center">
                <div className="text-sm">
                  <div className="flex items-center gap-2 font-medium text-slate-900">
                    <ListTree className="h-4 w-4 text-slate-500" />
                    <FormattedMessage
                      id="reprocessDocument.preserveStructure.title"
                      defaultMessage="Preserve Original Structure"
                    />
                  </div>
                  <p className="text-slate-500 mt-1 text-xs">
                    <FormattedMessage
                      id="reprocessDocument.preserveStructure.description"
                      defaultMessage="Use the embedded Table of Contents from the source file."
                    />
                  </p>
                  {!hasBookmarks && (
                    <p className="text-red-500 text-xs font-semibold mt-1">
                      <FormattedMessage
                        id="reprocessDocument.preserveStructure.noBookmarks"
                        defaultMessage="No bookmarks detected in source file."
                      />
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Option B: Smart Auto-Categorization (USE_SNOWFLAKE) */}
          <div
            onClick={() => handleOptionClick("USE_SNOWFLAKE")}
            className={`
              relative flex cursor-pointer rounded-lg border p-4 shadow-sm focus:outline-none
              ${mode === "USE_SNOWFLAKE" ? "border-sky-600 ring-1 ring-sky-600 bg-sky-50" : "border-slate-300 hover:bg-slate-50"}
            `}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                handleOptionClick("USE_SNOWFLAKE");
              }
            }}
          >
            <div className="flex w-full items-center justify-between">
              <div className="flex items-center">
                <div className="text-sm">
                  <div className="flex items-center gap-2 font-medium text-slate-900">
                    <Sparkles className="h-4 w-4 text-sky-500" />
                    <FormattedMessage
                      id="reprocessDocument.smartCategorization.title"
                      defaultMessage="Smart Auto-Categorization"
                    />
                  </div>
                  <p className="text-slate-500 mt-1 text-xs">
                    <FormattedMessage
                      id="reprocessDocument.smartCategorization.description"
                      defaultMessage="Let Sky AI reorganize pages into standardized folders (Medical, Legal, etc)."
                    />
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {error && <div className="text-sm text-red-500 mb-2">{error}</div>}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => setOpen(false)}
            disabled={isLoading}
          >
            <FormattedMessage id="cancel" defaultMessage="Cancel" />
          </Button>
          <Button onClick={handleSubmit} disabled={isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                <FormattedMessage
                  id="reprocessDocument.button.confirming"
                  defaultMessage="Reprocessing..."
                />
              </>
            ) : (
              <FormattedMessage
                id="reprocessDocument.button.confirm"
                defaultMessage="Reprocess"
              />
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
